import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const RegisterPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);

  // Read redirect intent from URL — e.g. /register?redirect=ai-filing
  const params       = new URLSearchParams(location.search);
  const redirectTo   = params.get("redirect");
  const isAIRedirect = redirectTo === "ai-filing";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const cleanPhone = formData.phone.replace(/[\s\-\+]/g, "");
    if (formData.phone && !/^[6-9]\d{9}$/.test(cleanPhone)) {
      toast.error("Please enter a valid 10-digit Indian mobile number");
      return;
    }

    setLoading(true);
    try {
      await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        user_type: "client",
      });
      toast.success("Account created! Welcome to MoolTax.");

      // Phase 2 — if user came via AI Filing CTA, redirect there
      if (isAIRedirect) {
        navigate("/ai-filing", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Registration failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-6 py-12"
      style={{ backgroundColor: "var(--secondary)" }}
    >
      <div
        className="w-full max-w-5xl grid md:grid-cols-2 overflow-hidden rounded-2xl border bg-white shadow-sm"
        style={{ borderColor: "var(--border)" }}
      >
        {/* Left: Form */}
        <div className="p-10 md:p-12 flex flex-col justify-center">
          <div className="mb-8">
            <div
              className="inline-flex items-center rounded-full px-3 py-1 text-sm mb-4"
              style={{ backgroundColor: "var(--secondary)" }}
            >
              {isAIRedirect ? "Create account to start AI Filing" : "Create Client Account"}
            </div>
            <h1
              className="text-4xl font-bold leading-tight mb-3"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Start your MoolTax journey
            </h1>
            <p className="text-base opacity-75 leading-7">
              {isAIRedirect
                ? "Create your free account and you will be taken directly to AI Tax Filing — upload your documents and get your ITR JSON in minutes."
                : "Create your account to begin your tax filing process, manage documents, and track your case from start to finish."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium mb-2">Full name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                autoComplete="name"
                className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                style={{ borderColor: "var(--border)" }}
                placeholder="Rahul Sharma"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Email address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                style={{ borderColor: "var(--border)" }}
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Phone number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                autoComplete="tel"
                className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                style={{ borderColor: "var(--border)" }}
                placeholder="+91 98765 43210"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                style={{ borderColor: "var(--border)" }}
                placeholder="At least 8 characters"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Confirm password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
                className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2"
                style={{ borderColor: "var(--border)" }}
                placeholder="Repeat your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-3 rounded-lg disabled:opacity-50"
            >
              {loading
                ? "Creating account..."
                : isAIRedirect
                ? "Create Account & Start AI Filing"
                : "Create Account"}
            </button>
          </form>

          <p className="text-sm opacity-65">
            Already have an account?{" "}
            <Link
              to={isAIRedirect ? "/login?redirect=ai-filing" : "/login"}
              className="underline"
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Right: Info panel */}
        <div
          className="hidden md:flex flex-col justify-center p-12"
          style={{
            background: "linear-gradient(135deg, var(--secondary) 0%, #ffffff 100%)",
          }}
        >
          <div
            className="rounded-2xl border bg-white p-8"
            style={{ borderColor: "var(--border)" }}
          >
            {isAIRedirect ? (
              // AI Filing context panel
              <>
                <h2
                  className="text-2xl font-semibold mb-4"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  File your ITR in minutes with AI
                </h2>
                <p className="opacity-75 leading-7 mb-6">
                  After creating your account you will land directly on the
                  AI Filing page. Upload your documents and our AI handles
                  everything — no CA needed.
                </p>
                <div className="space-y-4">
                  {[
                    { title: "Upload documents",    desc: "Form 16, 26AS, bank statements & more" },
                    { title: "AI analyses",          desc: "Reads every document, computes tax both regimes" },
                    { title: "Best regime selected", desc: "Old vs New — AI picks the one with lower tax" },
                    { title: "Download ITR JSON",    desc: "File directly on incometax.gov.in yourself" },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4"
                      style={{ backgroundColor: "var(--secondary)" }}
                    >
                      <div className="font-medium mb-1">{item.title}</div>
                      <div className="text-sm opacity-75">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              // Default context panel — unchanged from Phase 1
              <>
                <h2
                  className="text-2xl font-semibold mb-4"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  A smoother tax filing experience
                </h2>
                <p className="opacity-75 leading-7 mb-6">
                  Get started with a clean, guided workflow built to reduce
                  confusion and help you complete your filing with confidence.
                </p>
                <div className="space-y-4">
                  {[
                    { title: "Create your request",    desc: "Choose the right plan and start in minutes." },
                    { title: "Upload required documents", desc: "Securely submit only what is needed for your case." },
                    { title: "Stay informed",          desc: "Track progress and receive updates in one place." },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="rounded-xl p-4"
                      style={{ backgroundColor: "var(--secondary)" }}
                    >
                      <div className="font-medium mb-1">{item.title}</div>
                      <div className="text-sm opacity-75">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;